import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { useCallback, useState } from "react";

// Utility function to sanitize image URLs for logging purposes.
// It is needed because some image URLs may contain sensitive information (e.g., tokens) that should not be logged.
const sanitizeImageUrlForLogging = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    url = `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    // Ignore invalid URLs
  }
  return url.replace(/=[^&#]*/g, "=***");
};

type UseFailedImageUrlsResult = {
  failedImageUrls: Set<string>;
  markImageAsFailed: (url: string) => void;
  isImageFailed: (url: string) => boolean;
};

export function useFailedImageUrls(): UseFailedImageUrlsResult {
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
    () => new Set()
  );

  const markImageAsFailed = useCallback((url: string) => {
    setFailedImageUrls((previousFailedImageUrls) => {
      if (previousFailedImageUrls.has(url)) {
        return previousFailedImageUrls;
      }

      const sanitizedImageUrl = sanitizeImageUrlForLogging(url);
      reportMessageToSentry(
        `Failed to download task image from upload '${sanitizedImageUrl}'`,
        {
          error: new Error(
            `Failed to download image from URL: ${sanitizedImageUrl}`
          ),
        },
        "error"
      );

      const nextFailedImageUrls = new Set(previousFailedImageUrls);
      nextFailedImageUrls.add(url);
      return nextFailedImageUrls;
    });
  }, []);

  const isImageFailed = useCallback(
    (url: string) => failedImageUrls.has(url),
    [failedImageUrls]
  );

  return {
    failedImageUrls,
    markImageAsFailed,
    isImageFailed,
  };
}
